import {Tooltip} from 'platform/components';
import {HStack, Show} from 'platform/foundation';

import {Fragment, MouseEvent, useContext} from 'react';
import {Link} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {useDataGridContext} from '../../context/useDataGridContext';
import {EagGridConfigReactContext} from '../../context/useEagGridConfig';
import {CellRendererProps} from '../../types/CellRendererProps';
import {TooltipGetter} from '../../types/TooltipGetter';
import {getCellClassName} from '../../utils/getCellClassName';
import {getTooltipProps} from '../../utils/getTooltipProps';
import {EmptyCellValue} from '../EmptyCellValue/EmptyCellValue';

// `url` is optional: real BE ENTITY cells carry {id, label, resourceId?,
// additionalParameters?} instead — those resolve to a route via the host's
// entityResourceResolver (item resourceId falls back to the column's, like the
// legacy datagrid). Items neither carrying a url nor resolving to one render as
// plain-text labels rather than anchors.
type LinkValue = {
  label: string;
  url?: string;
  id?: string;
  resourceId?: string;
  additionalParameters?: Record<string, string>;
} | null;

// isArray columns deliver an array of link items in a single cell
type LinkCellRendererProps = CellRendererProps<LinkValue | Array<LinkValue>> & {
  getTooltipProps?: TooltipGetter;
};

// stopPropagation on the React synthetic event so the row's onClick handler
// (which performs row navigation) does not fire. Setting a native onclick via
// ref would stop the native bubble before React's root delegation receives it,
// which silently breaks Link's client-side navigation.
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

export function LinkCellRenderer(props: LinkCellRendererProps) {
  const gridContext = useDataGridContext();
  // Optional read (not the strict useEagGridConfig hook): renderers must also
  // work outside the provider (tests, isolated rendering) — no resolver then.
  const gridConfig = useContext(EagGridConfigReactContext);
  const className = getCellClassName(props.isWrap);
  const tooltipProps = getTooltipProps(props.row, props.getTooltipProps);

  if (isNil(props.value)) {
    return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} />;
  }

  const entityResourceResolver = gridConfig?.entityResourceResolver;

  // Explicit url wins; otherwise resolve the entity to a host route the way the
  // legacy datagrid did: resolver(item.resourceId ?? column.resourceId, id, params).
  const resolveUrl = (item: NonNullable<LinkValue>): string | undefined => {
    if (isNotNil(item.url)) {
      return item.url;
    }
    const resource = item.resourceId ?? props.column.resourceId;
    if (isNil(entityResourceResolver) || isNil(item.id) || isNil(resource)) {
      return undefined;
    }
    return entityResourceResolver(resource, item.id, item.additionalParameters);
  };

  const linkClickOverride = gridContext?.linkClickOverride;
  // Only called for items that DO have a url — url-less items render as plain
  // text below and never get a click handler.
  const buildClickHandler = (item: {label: string; url: string}) =>
    isNotNil(linkClickOverride)
      ? (event: MouseEvent) => {
          // preventDefault suppresses the default Link navigation; the override
          // decides the target itself (e.g. FE-composed detail routes).
          event.preventDefault();
          event.stopPropagation();
          linkClickOverride({
            rowData: props.row,
            defaultRedirectRoute: item.url,
            value: item.label,
          });
        }
      : stopPropagation;

  // isArray columns: one link per item. Items without a label carry no renderable
  // data and are skipped (parity with toPlainLabel on the PLAIN route).
  const items = (Array.isArray(props.value) ? props.value : [props.value]).filter(
    (item): item is NonNullable<LinkValue> => isNotNil(item) && isNotNil(item.label)
  );

  // Nothing renderable (nil value handled above; also [] / all-nil / all-label-less
  // arrays): EmptyCellValue carries no tooltip-bearing wrapper.
  if (items.length === 0) {
    return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} />;
  }

  return (
    <Tooltip {...tooltipProps}>
      <div className={className}>
        <HStack wrap={props.isWrap}>
          {items.map((item, index) => {
            const url = resolveUrl(item);
            return (
              // eslint-disable-next-line react/no-array-index-key -- Pre-existing violation
              <Fragment key={index}>
                {/* The ' / ' separator lives OUTSIDE the anchors: it is neither
                    clickable nor part of a link's accessible name. whitespace-pre
                    keeps the surrounding spaces from collapsing at the inline
                    boundaries of the flex items. */}
                <Show when={index > 0}>
                  <span className="whitespace-pre">{' / '}</span>
                </Show>
                {isNotNil(url) ? (
                  // The legacy datagrid styled cell anchors via its global grid
                  // theme (a { color: #0065ff } + underline on hover); eag-grid
                  // has no such theme layer, so the link look lives here.
                  // `!` is load-bearing: ComponentsProvider's ResetCss sets
                  // `a { color: inherit; text-decoration: inherit }` as UNLAYERED
                  // styled-components CSS, which outranks Tailwind's layered
                  // utilities — only !important wins over it.
                  <Link
                    to={url}
                    className="text-[#0065FF]! hover:underline!" // eslint-disable-line react/forbid-component-props -- Pre-existing violation
                    onClick={buildClickHandler({label: item.label, url})}
                  >
                    {item.label}
                  </Link>
                ) : (
                  // An entity with no url and no resolvable route (nothing to
                  // navigate to): the label is still real data, so render it as
                  // plain text rather than an anchor that would resolve to the
                  // current page.
                  <span>{item.label}</span>
                )}
              </Fragment>
            );
          })}
        </HStack>
      </div>
    </Tooltip>
  );
}
